import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { getEmpresasUsuario, getAtributosUsuario } from '@/common/utils/services/usuarios.service'
import { getIdUsuario } from '@/auth/auth.utils'
import { getIdCuenta } from '../util/idCuenta'
import { tienePerfilMedico } from '../constants/medicoEstados'
import { formatearCuit, normalizarCuit } from '../../seleccion-cuenta/util/formatearCuit'

// Atributos que indican que el usuario no debe ver "Mis cuentas"
const ATRIBUTO_ADMIN_TOTAL = 'ADMINTOTAL'
const ATRIBUTO_ADMIN_HYS = 'ADMINHYS'
const VALOR_SI = 'S'

/**
 * Verifica si el usuario es Admin ART o Admin HYS basado en sus atributos
 * @param {Array} atributos - Lista de atributos del usuario
 * @returns {boolean} true si es Admin ART o Admin HYS
 */
const verificarEsAdminARToHyS = (atributos) => {
  if (!Array.isArray(atributos) || atributos.length === 0) {
    return false
  }
  return atributos.some(
    (attr) =>
      (attr.atributo === ATRIBUTO_ADMIN_TOTAL && attr.valor === VALOR_SI) ||
      (attr.atributo === ATRIBUTO_ADMIN_HYS && attr.valor === VALOR_SI)
  )
}

/**
 * Hook para obtener y gestionar las cuentas (contratos + solicitudes) del usuario
 * @param {Array<{contrato: number|string}>} contratosExcluidos - Cuentas con baja
 * pendiente de guardar: se excluyen de la grilla ANTES de filtrar y paginar, para
 * que la paginación y el total reflejen el estado visual real.
 * @returns {Object} Estado y funciones para la grilla de cuentas
 */
export const useCuentasUsuario = (contratosExcluidos = []) => {
  const [contratos, setContratos] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchValue, setSearchValue] = useState('')
  // null = aún no se conoce el perfil; true/false = ya se resolvió
  const [esAdminARToHyS, setEsAdminARToHyS] = useState(null)

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Secuencia de requests: descarta respuestas obsoletas si un refresh se
  // solapa con otro (mismo patrón que useUsuarios en administracion-usuario_front).
  const requestSequence = useRef(0)
  const controllerRef = useRef(null)

  // Cargar contratos + atributos (reutilizable para refrescar la grilla)
  const fetchDatos = useCallback(async () => {
    const idUsuario = getIdUsuario()
    if (!idUsuario) {
      setError('No se encontró el ID de usuario')
      return
    }

    const sequence = ++requestSequence.current
    const controller = new AbortController()
    controllerRef.current = controller

    setIsLoading(true)
    setError(null)

    try {
      // Obtener empresas (cuentas con estado, administrador y perfil médico) y
      // atributos en paralelo
      const [resultContratos, resultAtributos] = await Promise.all([
        getEmpresasUsuario(Number(idUsuario), { signal: controller.signal }),
        getAtributosUsuario(Number(idUsuario), 1, null, { signal: controller.signal }),
      ])

      // Llegó una respuesta de un request viejo: se descarta
      if (sequence !== requestSequence.current) return

      // Procesar contratos
      if (resultContratos.code === 200) {
        setContratos(resultContratos.data)
      } else {
        setError(resultContratos.message)
        setContratos([])
      }

      // Procesar atributos para determinar si es Admin ART o HYS
      if (resultAtributos.code === 200) {
        const esAdmin = verificarEsAdminARToHyS(resultAtributos.data)
        setEsAdminARToHyS(esAdmin)
      } else {
        // Si falla la consulta de atributos, se muestra la sección por defecto
        setEsAdminARToHyS(false)
      }
    } catch (error) {
      if (sequence !== requestSequence.current) return
      console.error('Error al cargar los contratos del usuario:', error)
      setError('No se pudieron cargar las cuentas del usuario.')
      setContratos([])
    } finally {
      if (sequence === requestSequence.current) setIsLoading(false)
    }
  }, [])

  // Cargar datos al montar; cancela el request en curso al desmontar
  useEffect(() => {
    fetchDatos()
    return () => controllerRef.current?.abort()
  }, [fetchDatos])

  // Excluir las cuentas con baja pendiente (antes de filtrar y paginar).
  // Se compara por identificador estable (contrato o idSolicitud), no sólo por
  // contrato: las solicitudes pendientes pueden no tener contrato.
  const contratosVisibles = useMemo(() => {
    if (!contratosExcluidos.length) return contratos
    const excluidosSet = new Set(contratosExcluidos.map((c) => getIdCuenta(c)))
    return contratos.filter((c) => !excluidosSet.has(getIdCuenta(c)))
  }, [contratos, contratosExcluidos])

  // Filtrar por búsqueda. La CUIT se busca por dígitos (con o sin guiones),
  // ya que en la grilla se muestra formateada (XX-XXXXXXXX-X).
  const contratosFiltrados = useMemo(() => {
    if (!searchValue.trim()) return contratosVisibles
    const searchLower = searchValue.toLowerCase()
    const searchCuit = normalizarCuit(searchValue)
    return contratosVisibles.filter(
      (c) =>
        c.razonSocial?.toLowerCase().includes(searchLower) ||
        (searchCuit && normalizarCuit(c.cuit).includes(searchCuit)) ||
        String(c.contrato).includes(searchLower)
    )
  }, [contratosVisibles, searchValue])

  // Resetear página al buscar
  useEffect(() => {
    setCurrentPage(1)
  }, [searchValue])

  // Paginación
  const datosPaginados = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return contratosFiltrados.slice(startIndex, endIndex)
  }, [contratosFiltrados, currentPage, pageSize])

  const headers = useMemo(() => ({ 'x-total-count': contratosFiltrados.length }), [contratosFiltrados])

  const handlePageChange = useCallback(
    ({ page, pageSize: newPageSize }) => {
      if (newPageSize && newPageSize !== pageSize) {
        setPageSize(newPageSize)
        setCurrentPage(1)
      } else if (page) {
        setCurrentPage(page)
      }
    },
    [pageSize]
  )

  // La visibilidad de las columnas Médico / Subir documento se evalúa sobre
  // TODAS las cuentas asociadas, no solo las de la página actual de la grilla.
  const tieneAlgunaCuentaMedica = useMemo(() => contratos.some((c) => tienePerfilMedico(c.medico)), [contratos])

  return {
    // Lista completa de contratos + solicitudes (sin paginar ni filtrar),
    // usada para validar CUITs duplicadas desde el modal de asociación
    todasLasCuentas: contratos,
    contratos: datosPaginados,
    headers,
    isLoading,
    error,
    searchValue,
    setSearchValue,
    onPageChange: handlePageChange,
    esAdminARToHyS,
    tieneAlgunaCuentaMedica,
    refreshContratos: fetchDatos,
  }
}
